import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { celebrateMilestone, dismissCelebration } from "./cashierApi";

/**
 * "🎉 {name} reached {milestone} — [Celebrate]" on the cashier board.
 *
 * This is the visible half of the F-14 fix. The milestone cheer used to be
 * raised by the hourly automation sweep, which reads visit_count off a table
 * and therefore fired whenever it happened to run — routinely after the guest
 * had paid, left and driven home. It is raised at Mark-paid now, which is both
 * the instant the tier is genuinely reached (a visit is a cashier-witnessed
 * PAID visit) and the one moment a human is standing in front of the guest.
 *
 * It is a TAP and not an automatic push by default, deliberately. Confetti on
 * every screen in the venue is wrong if the guest is mid-argument with the card
 * reader or halfway out of the door, and the only person who can see that is
 * the cashier. A venue that wants it automatic switches the milestone trigger
 * to 'auto' and never sees this banner.
 *
 * "Not now" is not decoration: without it the banner sits on the board for its
 * whole offer window while the cashier serves the next five people.
 */
export default function CelebrationBanner({ token, celebrations, onResolved }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState({});
  const [fired, setFired] = useState({});

  if (!celebrations?.length) return null;

  const act = async (c, run) => {
    if (busy[c.id]) return;
    setBusy((b) => ({ ...b, [c.id]: true }));
    try {
      await run(token, c.id);
      // Mark it locally so the banner reacts on the tap rather than on the next
      // poll — the cashier has to see that their tap landed.
      setFired((f) => ({ ...f, [c.id]: true }));
      onResolved?.(c.id);
    } catch (_) {
      setBusy((b) => { const n = { ...b }; delete n[c.id]; return n; });
    }
  };

  return (
    <div className="cds-cheers">
      {celebrations.map((c) => (
        <div key={c.id} className={`cds-cheer${fired[c.id] ? " done" : ""}`}>
          <span className="ic" aria-hidden="true">🎉</span>
          <div className="tx">
            <div className="hi">
              {t("cashier.cheer.headline", { name: c.name || t("cashier.pts.no_name"), visits: c.visits })}
            </div>
            {c.bonus > 0 && (
              <div className="sub">{t("cashier.cheer.bonus", { points: c.bonus })}</div>
            )}
          </div>

          {fired[c.id] ? (
            <span className="cds-cheer-done">✓ {t("cashier.cheer.sent")}</span>
          ) : (
            <div className="cds-cheer-btns">
              <button type="button" className="cds-cheer-go" disabled={!!busy[c.id]}
                onClick={() => act(c, celebrateMilestone)}>
                {busy[c.id] ? <span className="cds-spin" /> : t("cashier.cheer.celebrate")}
              </button>
              <button type="button" className="cds-cheer-skip" disabled={!!busy[c.id]}
                onClick={() => act(c, dismissCelebration)}>
                {t("cashier.cheer.not_now")}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
